import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardShell } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { ReportFilters, StatCard, useReportRange } from "@/components/ReportFilters";
import { buildRange, downloadCsv, fetchAdminReport } from "@/lib/reports";

function AdminReportsPage() {
  const { preset, from, to, onChange } = useReportRange("30d");
  const range = useMemo(() => buildRange(preset, from, to), [preset, from, to]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-report", range.from, range.to],
    queryFn: () => fetchAdminReport(range),
  });

  const exportCsv = () => {
    if (!data) return;
    const rows: (string | number)[][] = [
      ["Landoo Admin Report"],
      ["Period", range.label],
      [],
      ["Metric", "Count"],
      ["Total Customers", data.totalCustomers],
      ["Total Partners", data.totalPartners],
      ["Total Listings", data.totalListings],
      ["Approved Listings", data.approvedListings],
      ["Pending Listings", data.pendingListings],
      ["Rejected Listings", data.rejectedListings],
      ["Total Enquiries", data.totalEnquiries],
      [],
      ["Service", "Total", "Approved", "Pending", "Rejected"],
      ...data.byService.map((s) => [s.label, s.total, s.approved, s.pending, s.rejected]),
    ];
    downloadCsv(`landoo-admin-report-${range.from.slice(0, 10)}.csv`, rows);
  };

  return (
    <DashboardShell title="Reports" role="Admin" items={ADMIN_NAV}>
      <div className="space-y-5">
        <ReportFilters
          preset={preset}
          from={from}
          to={to}
          onChange={onChange}
          onExportCsv={exportCsv}
        />
        <p className="text-sm text-muted-foreground">Showing data for {range.label}</p>

        {error && (
          <p className="text-sm text-coral">Could not load reports. {(error as Error).message}</p>
        )}
        {isLoading && <p className="text-sm text-muted-foreground">Loading report…</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Total Customers" value={data.totalCustomers} />
              <StatCard label="Total Partners" value={data.totalPartners} />
              <StatCard label="Total Listings" value={data.totalListings} />
              <StatCard label="Approved Listings" value={data.approvedListings} />
              <StatCard label="Pending Listings" value={data.pendingListings} />
              <StatCard label="Rejected Listings" value={data.rejectedListings} />
              <StatCard label="Total Enquiries" value={data.totalEnquiries} />
            </div>

            <div className="rounded-xl border border-border bg-card">
              <div className="border-b border-border px-4 py-3 text-sm font-semibold">
                Listings by service
              </div>
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 font-medium">Service</th>
                      <th className="px-4 py-2 font-medium">Total</th>
                      <th className="px-4 py-2 font-medium">Approved</th>
                      <th className="px-4 py-2 font-medium">Pending</th>
                      <th className="px-4 py-2 font-medium">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byService.map((s) => (
                      <tr key={s.service} className="border-t border-border">
                        <td className="px-4 py-2.5 font-medium">{s.label}</td>
                        <td className="px-4 py-2.5">{s.total}</td>
                        <td className="px-4 py-2.5">{s.approved}</td>
                        <td className="px-4 py-2.5">{s.pending}</td>
                        <td className="px-4 py-2.5">{s.rejected}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="divide-y divide-border md:hidden">
                {data.byService.map((s) => (
                  <div key={s.service} className="px-4 py-3">
                    <div className="font-medium">{s.label}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Total {s.total} · Approved {s.approved} · Pending {s.pending} · Rejected{" "}
                      {s.rejected}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardShell>
  );
}

export const Route = createFileRoute("/_authenticated/admin/reports")({
  head: () => ({
    meta: [
      { title: "Reports · Landoo Admin" },
      {
        name: "description",
        content: "Customer, partner, listing and enquiry reports for the Landoo admin console.",
      },
      { property: "og:title", content: "Reports · Landoo Admin" },
      {
        property: "og:description",
        content: "Customer, partner, listing and enquiry reports for the Landoo admin console.",
      },
    ],
  }),
  component: AdminReportsPage,
});
